import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  InputAdornment,
  CircularProgress,
  Alert,
  Select,
  TableSortLabel,
  Menu,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import {
  PageContainer,
  CardContainer,
  FiltersContainer,
  TitleWrap,
  TableContainerStyled,
  FooterContainer,
} from "./Styles";

import LogsService from "../../Endpoints/LogsService";
import ExportComponent from "../../components/Exports/ExporterFiles";
import ExportIconButton from "../Generals/Buttons/ExportIconButton";
import IconComponent from "../Icons/IconComponent.js";
import {
  buildTableRowsPerPageOptions,
  TABLE_COMPACT_HEADER_HEIGHT,
  TABLE_COMPACT_HEADER_PADDING,
  TABLE_HEADER_FONT_SIZE,
  TABLE_INITIAL_ROWS_PER_PAGE,
} from "../Table/constants.js";

/* ---------------- OPTIONS ---------------- */

const LOG_CODES = [
  "All",
  "Completed",
  "Failed",
  "Unexpected error",
  "Warning",
  "Extended trade",
];

const PERIODS = [
  "Today",
  "Last 3 days",
  "Last week",
  "Last month",
  "Last 3 months",
  "Last 6 months",
  "Custom date",
];

const LOG_CODE_OPTIONS = LOG_CODES.map((v) => ({ value: v, label: v }));
const PERIOD_OPTIONS = PERIODS.map((v) => ({ value: v, label: v }));

const LOG_EXPORT_COLUMNS = [
  { field: "status", headerName: "Status", label: "Status" },
  { field: "date", headerName: "Date", label: "Date" },
  { field: "origin", headerName: "Origin", label: "Origin" },
  { field: "description", headerName: "Description", label: "Description" },
];

/* ---------------- STYLES ---------------- */

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "10px", height: 40 },
  "& .MuiInputLabel-root": { fontSize: 13, top: -6 },
};

const searchSx = {
  width: { xs: "100%", sm: 260 },
  minWidth: { sm: 220 },
  maxWidth: { lg: 320 },
  flex: { lg: "1 1 240px" },
  ...fieldSx,
};
const selectSx = {
  width: { xs: "100%", sm: 240 },
  minWidth: { sm: 200 },
  maxWidth: { lg: 300 },
  flex: { lg: "1 1 220px" },
  ...fieldSx,
};
const dateSx = {
  width: 180,
  ...fieldSx,
  "& .MuiInputBase-input": {
    whiteSpace: "nowrap",
  },
};
const menuItemSx = { py: 1.4, px: 3, fontSize: 15 };

const exportButtonSx = {
  textTransform: "none",
  fontWeight: 800,
  fontSize: 15,
  height: "40px",
  minHeight: "40px",
  padding: "12px",
  boxShadow: "none",
  borderRadius: "8px",
  backgroundColor: "#EB0000",
  color: "#FFFFFF",
  borderColor: "#EB0000",
  flex: { xs: "1 1 140px", sm: "0 0 auto" },
  "&:hover": {
    backgroundColor: "#D50000",
    borderColor: "#D50000",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#EB0000",
    color: "#FFFFFF",
    opacity: 0.5,
  },
};

/* ---------------- HELPERS ---------------- */

function toUnixSeconds(date) {
  return Math.floor(date.getTime() / 1000);
}

function startOfDay(date) {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0)
  );
}

function endOfDay(date) {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 0)
  );
}

function addDays(date, amount) {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + amount);
  return next;
}

function formatDateInput(date) {
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, "0");
  const day = `${date.getUTCDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseDateInputUtc(value) {
  const [year, month, day] = String(value).split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function parseLogDate(value) {
  const raw = String(value || "").trim();
  const match = raw.match(
    /^(\d{4})\.(\d{2})\.(\d{2})(?:\s+(\d{2}):(\d{2}):(\d{2})(?::(\d{1,3}))?)?$/
  );

  if (!match) return null;

  const [, year, month, day, hh = "00", mm = "00", ss = "00", ms = "0"] = match;

  return new Date(Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hh),
    Number(mm),
    Number(ss),
    Number(ms.padEnd(3, "0"))
  ));
}

function getPeriodRange(selectedPeriod) {
  const now = new Date();

  switch (selectedPeriod) {
    case "Today":
      return {
        from: startOfDay(now),
        to: endOfDay(now),
      };
    case "Last 3 days":
      return {
        from: startOfDay(addDays(now, -2)),
        to: endOfDay(now),
      };
    case "Last week":
      return {
        from: startOfDay(addDays(now, -6)),
        to: endOfDay(now),
      };
    case "Last month":
      return {
        from: startOfDay(addDays(now, -29)),
        to: endOfDay(now),
      };
    case "Last 3 months":
      return {
        from: startOfDay(addDays(now, -89)),
        to: endOfDay(now),
      };
    case "Last 6 months":
      return {
        from: startOfDay(addDays(now, -179)),
        to: endOfDay(now),
      };
    default:
      return null;
  }
}

function getPeriodUnixRange(selectedPeriod, customFrom, customTo) {
  if (selectedPeriod === "Custom date") {
    if (!customFrom || !customTo) return null;

    const fromDate = parseDateInputUtc(customFrom);
    const toDate = parseDateInputUtc(customTo);

    if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
      return null;
    }

    return {
      from: toUnixSeconds(startOfDay(fromDate)),
      to: toUnixSeconds(endOfDay(toDate)),
    };
  }

  const range = getPeriodRange(selectedPeriod);
  if (!range) return null;

  return {
    from: toUnixSeconds(range.from),
    to: toUnixSeconds(range.to),
  };
}

function filterAndSortLogs(logs, search, logCode, dateSortDirection) {
  const query = String(search ?? "").trim().toLowerCase();
  const selectedCode = String(logCode ?? "").trim();

  let out = Array.isArray(logs) ? logs : [];

  if (query) {
    out = out.filter((row) =>
      `${row.status} ${row.date} ${row.origin} ${row.description} ${row.code}`
        .toLowerCase()
        .includes(query)
    );
  }

  if (selectedCode && selectedCode !== "All") {
    const selected = selectedCode.toLowerCase();
    out = out.filter((row) => {
      const status = String(row.status ?? "").toLowerCase();
      const code = String(row.code ?? "").toLowerCase();
      return status.includes(selected) || code.includes(selected);
    });
  }

  return [...out].sort((a, b) => {
    const dateA = parseLogDate(a.date)?.getTime();
    const dateB = parseLogDate(b.date)?.getTime();
    const validA = Number.isFinite(dateA);
    const validB = Number.isFinite(dateB);

    if (!validA && !validB) return 0;
    if (!validA) return 1;
    if (!validB) return -1;

    return dateSortDirection === "asc" ? dateA - dateB : dateB - dateA;
  });
}

function mapApiCodeToStatus(code) {
  const c = String(code ?? "").toUpperCase();
  if (c === "OK" || c.includes("SUCCESS")) return "Processed successfully";
  if (c.includes("WARN")) return "Warning";
  if (c.includes("PENDING") || c.includes("IN_PROCESS") || c.includes("PROCESS"))
    return "Pending/In process";
  if (c.includes("UNEXPECTED")) return "Unexpected error";
  if (c.includes("EXTENDED")) return "Extended trade";
  if (!c) return "Unknown";
  return "Failed/Rejected";
}

const getStatusSx = (status) => {
  const s = (status || "").toLowerCase();

  if (s.includes("processed")) {
    return {
      bgcolor: "rgba(46, 204, 113, 0.12)",
      color: "#1F7A46",
      border: "1px solid rgba(46, 204, 113, 0.25)",
    };
  }
  if (s.includes("pending") || s.includes("process")) {
    return {
      bgcolor: "rgba(241, 196, 15, 0.14)",
      color: "#8A6D00",
      border: "1px solid rgba(241, 196, 15, 0.28)",
    };
  }
  if (s.includes("failed") || s.includes("rejected") || s.includes("error")) {
    return {
      bgcolor: "rgba(231, 76, 60, 0.12)",
      color: "#A1241A",
      border: "1px solid rgba(231, 76, 60, 0.25)",
    };
  }
  if (s.includes("warning")) {
    return {
      bgcolor: "rgba(241, 196, 15, 0.14)",
      color: "#8A6D00",
      border: "1px solid rgba(241, 196, 15, 0.28)",
    };
  }
  return {
    bgcolor: "rgba(149, 165, 166, 0.16)",
    color: "#4D5A5C",
    border: "1px solid rgba(149, 165, 166, 0.28)",
  };
};

const StatusPill = ({ value }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: 1.5,
      height: 28,
      borderRadius: 999,
      fontSize: 13,
      fontWeight: 700,
      whiteSpace: "nowrap",
      ...getStatusSx(value),
    }}
  >
    {value}
  </Box>
);

/* ---------------- PAGINATION UI (MISMO MODELO) ---------------- */

const ROWS_OPTIONS = buildTableRowsPerPageOptions([7, 10, 25, 50, 100]);

const LOG_HISTORY_COLUMNS = [
  { field: "status", label: "Status", minWidth: 180, width: 220 },
  { field: "date", label: "Date", minWidth: 180, width: 220, sortable: true },
  { field: "origin", label: "Origin", minWidth: 140, width: 180 },
  { field: "description", label: "Description", minWidth: 280, width: 510 },
];

const initialLogHistoryColumnWidths = LOG_HISTORY_COLUMNS.reduce(
  (widths, column) => {
    widths[column.field] = column.width;
    return widths;
  },
  {}
);
const logHistoryHeaderCellSx = {
  fontSize: TABLE_HEADER_FONT_SIZE,
  fontWeight: 700,
  color: "rgba(0,0,0,0.70)",
  backgroundColor: "#F9F9F9",
  borderBottom: 0,
  height: TABLE_COMPACT_HEADER_HEIGHT,
  padding: TABLE_COMPACT_HEADER_PADDING,
  lineHeight: "18px",
};

const arrowBtnSx = (disabled) => ({
  minWidth: 30,
  height: 30,
  borderRadius: "10px",
  backgroundColor: "#FFFFFF",
  border: "1px solid #E5E7EB",
  color: disabled ? "#9CA3AF" : "#374151",
  boxShadow: "none",
  padding: 0,
  "&:hover": {
    backgroundColor: "#F9FAFB",
    borderColor: "#D1D5DB",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#FFFFFF",
    borderColor: "#E5E7EB",
    color: "#9CA3AF",
    opacity: 1,
  },
});

/* ---------------- COMPONENT ---------------- */

const LogHistory = () => {
  const [search, setSearch] = useState("");
  const [logCode, setLogCode] = useState("All");
  const [period, setPeriod] = useState("Today");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [columnWidths, setColumnWidths] = useState(
    initialLogHistoryColumnWidths
  );
  const [dateSortDirection, setDateSortDirection] = useState("desc");
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportOpen = Boolean(exportAnchorEl);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(TABLE_INITIAL_ROWS_PER_PAGE);

  const isCustomDate = period === "Custom date";
  const canApply =
    !loading &&
    Boolean(period) &&
    (period !== "Custom date" || Boolean(from && to));

  const handleClear = () => {
    setSearch("");
    setLogCode("All");
    setPeriod("");
    setFrom("");
    setTo("");
    setLogs([]);
    setErrorMsg("");
    setPage(0);
  };

  const handleApply = async () => {
    setErrorMsg("");

    const range = getPeriodUnixRange(period, from, to);
    if (!range) {
      setErrorMsg("Select a valid period of time.");
      return;
    }

    if (range.from > range.to) {
      setErrorMsg("From date must be earlier than To date.");
      return;
    }

    setLoading(true);

    try {
      const res = await LogsService.getLogs({
        type: "FULL",
        from: range.from,
        to: range.to,
      });
      if (!res) {
        setLogs([]);
        setErrorMsg("Failed to load logs history.");
        return;
      }
      const apiMessages = Array.isArray(res?.data?.data?.messages)
        ? res.data.data.messages
        : Array.isArray(res?.data?.messages)
        ? res.data.messages
        : [];

      const normalized = apiMessages.map((m) => ({
        status: mapApiCodeToStatus(m?.code),
        date: m?.date ?? "",
        origin: m?.origin ?? "",
        description: m?.message ?? "",
        code: m?.code ?? "",
      }));

      setLogs(normalized);
      setPage(0);
    } catch (e) {
      setErrorMsg(e?.message || "Error al consultar logs.");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(
    () => filterAndSortLogs(logs, search, logCode, dateSortDirection),
    [logs, search, logCode, dateSortDirection]
  );

  const totalCount = rows.length;

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / rowsPerPage));
  }, [totalCount, rowsPerPage]);

  useEffect(() => setPage(0), [rowsPerPage]);
  useEffect(() => setPage(0), [totalCount]);
  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  const pageItems = useMemo(() => {
    const last = totalPages;
    const current = page + 1;

    if (last <= 4) return Array.from({ length: last }, (_, i) => i + 1);

    if (current <= 2) return [1, 2, 3, 4, "…"];
    if (current >= last - 1) return ["…", last - 3, last - 2, last - 1, last];
    return ["…", current - 1, current, current + 1, "…"];
  }, [page, totalPages]);

  const columnCellSx = useMemo(() => {
    return LOG_HISTORY_COLUMNS.reduce((styles, column) => {
      const width = columnWidths[column.field] || column.width;
      const isDescription = column.field === "description";

      styles[column.field] = {
        width: isDescription ? "auto" : width,
        minWidth: isDescription ? column.minWidth : column.minWidth,
        ...(isDescription ? {} : { maxWidth: width }),
        overflow: isDescription ? "visible" : "hidden",
        textOverflow: isDescription ? "clip" : "ellipsis",
        whiteSpace: "nowrap",
      };

      return styles;
    }, {});
  }, [columnWidths]);

  const handleColumnResizeStart = useCallback(
    (event, column) => {
      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = columnWidths[column.field] || column.width;
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handlePointerMove = (moveEvent) => {
        const nextWidth = Math.max(
          column.minWidth,
          startWidth + moveEvent.clientX - startX
        );

        setColumnWidths((prev) => ({
          ...prev,
          [column.field]: nextWidth,
        }));
      };

      const handlePointerUp = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
    },
    [columnWidths]
  );

  const handleColumnResizeKeyDown = useCallback((event, column) => {
    const direction =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;

    if (!direction) return;

    event.preventDefault();
    event.stopPropagation();

    setColumnWidths((prev) => {
      const currentWidth = prev[column.field] || column.width;

      return {
        ...prev,
        [column.field]: Math.max(column.minWidth, currentWidth + direction * 16),
      };
    });
  }, []);

  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);

  const handleExport = (type) => {
    if (!rows.length) {
      handleExportClose();
      return;
    }

    const formattedDate = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const fileName = `logs_history_${formattedDate}`;

    if (type === "csv") ExportComponent.GenerateCSV(LOG_EXPORT_COLUMNS, rows, fileName);
    if (type === "pdf") ExportComponent.GeneratePDF(LOG_EXPORT_COLUMNS, rows, fileName);
    if (type === "txt") ExportComponent.GenerateTXT(LOG_EXPORT_COLUMNS, rows, fileName);

    handleExportClose();
  };

  return (
    <PageContainer>
      <TitleWrap sx={{ mb: 4 }}>
        <Typography
          sx={{
            fontFamily: "Plus Jakarta Sans",
            fontSize: "26px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "140%",
          }}
        >
          Logs history
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Access a detailed record of system events and activities.
        </Typography>
      </TitleWrap>

      <CardContainer elevation={2}>
        <FiltersContainer>
          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={searchSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            select
            size="small"
            label="Log codes"
            value={logCode}
            onChange={(e) => setLogCode(e.target.value)}
            sx={selectSx}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => (!selected ? "Select an option" : selected),
            }}
          >
            {LOG_CODE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value} sx={menuItemSx}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Period of time"
            value={period}
            onChange={(e) => {
              const v = e.target.value;
              setPeriod(v);
              if (v === "Custom date") {
                setFrom("");
                setTo("");
                return;
              }

              const range = getPeriodRange(v);
              if (range) {
                setFrom(formatDateInput(range.from));
                setTo(formatDateInput(range.to));
              } else {
                setFrom("");
                setTo("");
              }
            }}
            sx={selectSx}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => (!selected ? "Select an option" : selected),
            }}
          >
            {PERIOD_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value} sx={menuItemSx}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          {isCustomDate && (
            <>
              <TextField
                size="small"
                label="From"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                sx={dateSx}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="To"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                sx={dateSx}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.5,
              flexWrap: { xs: "wrap", md: "nowrap" },
              width: { xs: "100%", lg: "auto" },
              flexShrink: 0,
              ml: { lg: "auto" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Button
                variant="text"
                startIcon={<RefreshIcon />}
                onClick={handleClear}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: 15,
                  color: "#9CA3AF",
                  height: "40px",
                  minWidth: "auto",
                  padding: "12px",
                  "& .MuiButton-startIcon": {
                    color: "#9CA3AF",
                    mr: 0.75,
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "#6B7280",
                  },
                }}
                disabled={loading}
              >
                Clear
              </Button>

              <Button
                variant="outlined"
                disabled={!canApply}
                onClick={handleApply}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: 15,
                  height: "40px",
                  padding: "12px",
                  color: "#9CA3AF",
                  borderColor: "#C7CBD6",
                  borderRadius: "12px",
                  backgroundColor: "#F8F6FB",
                  boxShadow: "none",
                  px: 2.5,
                  "&:hover": {
                    borderColor: "#B8BEC9",
                    backgroundColor: "#F8F6FB",
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    color: "#9CA3AF",
                    borderColor: "#C7CBD6",
                    backgroundColor: "#F8F6FB",
                  },
                }}
              >
                {loading ? (
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={18} />
                    Loading
                  </Box>
                ) : (
                  "Apply"
                )}
              </Button>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                gap: 1.5,
                flexWrap: "wrap",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <ExportIconButton
                disabled={loading || !rows.length}
                onClick={handleExportClick}
                sx={exportButtonSx}
              >
                <IconComponent icon="download" size="16px" />
                Export
              </ExportIconButton>

              <Menu
                anchorEl={exportAnchorEl}
                open={exportOpen}
                onClose={handleExportClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem onClick={() => handleExport("csv")}>Export as CSV</MenuItem>
                <MenuItem onClick={() => handleExport("pdf")}>Export as PDF</MenuItem>
                <MenuItem onClick={() => handleExport("txt")}>Export as TXT</MenuItem>
              </Menu>
            </Box>
          </Box>
        </FiltersContainer>

        {!!errorMsg && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">{errorMsg}</Alert>
          </Box>
        )}

        <TableContainerStyled sx={{ mt: 0.25 }}>
          {loading ? (
            <Box
              sx={{
                height: 505,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
                background: "#fff",
              }}
            >
              <CircularProgress />
              <Typography sx={{ fontWeight: 800, color: "#6B7280" }}>
                Loading...
              </Typography>
            </Box>
          ) : (
            <Table
              stickyHeader
              sx={{
                width: "max-content",
                minWidth: "100%",
                tableLayout: "auto",
              }}
            >
              <colgroup>
                {LOG_HISTORY_COLUMNS.map((column) => (
                  <col
                    key={column.field}
                    style={{
                      width:
                        column.field === "description"
                          ? "auto"
                          : `${columnWidths[column.field] || column.width}px`,
                    }}
                  />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  {LOG_HISTORY_COLUMNS.map((column) => (
                    <TableCell
                      key={column.field}
                      sortDirection={
                        column.field === "date" ? dateSortDirection : false
                      }
                      sx={{
                        ...columnCellSx[column.field],
                        ...logHistoryHeaderCellSx,
                        position: "relative",
                        pr: TABLE_COMPACT_HEADER_PADDING,
                        userSelect: "none",
                      }}
                    >
                      {column.field === "date" ? (
                        <TableSortLabel
                          active
                          direction={dateSortDirection}
                          onClick={() => {
                            setDateSortDirection((current) =>
                              current === "asc" ? "desc" : "asc"
                            );
                            setPage(0);
                          }}
                          sx={{
                            lineHeight: "18px",
                            "& .MuiTableSortLabel-icon": { fontSize: 16 },
                          }}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        <Box
                          component="span"
                          title={column.label}
                          sx={{
                            display: "block",
                            lineHeight: "18px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {column.label}
                        </Box>
                      )}
                      <Box
                        component="span"
                        role="separator"
                        aria-label={`Resize ${column.label} column`}
                        aria-orientation="vertical"
                        aria-valuemin={column.minWidth}
                        aria-valuenow={columnWidths[column.field] || column.width}
                        tabIndex={0}
                        onPointerDown={(event) =>
                          handleColumnResizeStart(event, column)
                        }
                        onKeyDown={(event) =>
                          handleColumnResizeKeyDown(event, column)
                        }
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          width: "12px",
                          height: "100%",
                          cursor: "col-resize",
                          touchAction: "none",
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: "22%",
                            bottom: "22%",
                            left: "50%",
                            width: "2px",
                            transform: "translateX(-50%)",
                            borderRadius: "2px",
                            backgroundColor: "rgba(21, 32, 42, 0.22)",
                          },
                          "&:hover::after, &:focus-visible::after": {
                            backgroundColor: "#15202A",
                          },
                          "&:focus-visible": {
                            outline: "2px solid #15202A",
                            outlineOffset: "-2px",
                          },
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((r, idx) => (
                    <TableRow key={`${r.date}-${idx}`} hover>
                      <TableCell
                        sx={{
                          ...columnCellSx.status,
                          py: 1.6,
                          textAlign: "left",
                        }}
                      >
                        <StatusPill value={r.status} />
                      </TableCell>

                      <TableCell
                        sx={{
                          ...columnCellSx.date,
                          color: "text.secondary",
                          fontWeight: 600,
                          textAlign: "left",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r.date}
                      </TableCell>

                      <TableCell
                        sx={{
                          ...columnCellSx.origin,
                          color: "text.secondary",
                          fontWeight: 600,
                          textAlign: "left",
                        }}
                      >
                        {r.origin}
                      </TableCell>

                      <TableCell
                        sx={{
                          ...columnCellSx.description,
                          whiteSpace: "nowrap",
                          overflow: "visible",
                          fontWeight: 700,
                          color: "#3C3C3C",
                          textAlign: "left",
                        }}
                        title={r.description}
                      >
                        {r.description}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ p: 0 }}>
                      <Box
                        sx={{
                          height: 400,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            textAlign: "center",
                            gap: 1.2,
                            color: "#6B7280",
                            maxWidth: 460,
                            px: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: "50%",
                              border: "2px solid rgba(55,65,81,0.6)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 900,
                              fontSize: 18,
                              lineHeight: 1,
                              color: "rgba(55,65,81,0.75)",
                            }}
                          >
                            !
                          </Box>

                          <Typography sx={{ fontSize: 14, lineHeight: 1.4 }}>
                            Sorry, we didn't find any requests under
                            <br />
                            the filters you entered. Please try a
                            <br />
                            different one.
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainerStyled>

        <FooterContainer
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 400 }}>
            Total: <b style={{ color: "#111827", fontWeight: 600 }}>{totalCount}</b>
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
              Pages:&nbsp;<b style={{ color: "#111827" }}>{totalPages}</b>
            </Typography>

            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>Rows:</Typography>

            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              sx={{
                height: 32,
                minWidth: 70,
                borderRadius: 2,
                ".MuiSelect-select": { py: 0.5 },
              }}
              MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 0.5 } } }}
            >
              {ROWS_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Button
                size="small"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                sx={arrowBtnSx(page === 0)}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
              </Button>

              {pageItems.map((it, idx) => {
                if (it === "…") {
                  return (
                    <Typography
                      key={`dots-${idx}`}
                      sx={{ fontSize: 14, color: "#9CA3AF", px: 0.5 }}
                    >
                      …
                    </Typography>
                  );
                }

                const pageNumber = it;
                const isActive = pageNumber === page + 1;

                return (
                  <Button
                    key={`p-${pageNumber}`}
                    size="small"
                    onClick={() => setPage(pageNumber - 1)}
                    sx={{
                      minWidth: 28,
                      height: 28,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: isActive ? 900 : 700,
                      color: isActive ? "#111827" : "#6B7280",
                      backgroundColor: isActive ? "#EEF2FF" : "transparent",
                      "&:hover": { backgroundColor: isActive ? "#EEF2FF" : "#F3F4F6" },
                    }}
                  >
                    {pageNumber}
                  </Button>
                );
              })}

              <Button
                size="small"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                sx={arrowBtnSx(page >= totalPages - 1)}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </Button>
            </Box>
          </Box>
        </FooterContainer>
      </CardContainer>
    </PageContainer>
  );
};

export default LogHistory;